import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Layers, Loader2 } from 'lucide-react';
import useSermonAutosave from '../hooks/useSermonAutosave';
import { exportSermonAsMarkdown, exportSermonAsPdf, exportSermonManuscriptPdf } from '../utils/sermonExport';

import SermonPrepHeader from './sermon-prep/SermonPrepHeader';
import SermonResearchPanel from './sermon-prep/SermonResearchPanel';
import SermonOutlinePanel from './sermon-prep/SermonOutlinePanel';
import SermonDraftsDialog from './sermon-prep/SermonDraftsDialog';
import SermonTemplatesDialog from './sermon-prep/SermonTemplatesDialog';
import SermonIllustrationsDialog from './sermon-prep/SermonIllustrationsDialog';
import NotesPickerDialog from './sermon-prep/NotesPickerDialog';
import SermonManuscriptDialog from './sermon-prep/SermonManuscriptDialog';
import SermonSeriesPicker from './sermon-prep/SermonSeriesPicker';
import useSermonDrafts from './sermon-prep/useSermonDrafts';
import { exportSermonAsTxt } from './sermon-prep/sermonExportText';
import { cloneDefaultOutline } from './sermon-prep/defaults';
import SermonAiScaffold from './SermonAiScaffold';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Sermon Preparation Tool — premium pastor workspace combining AI biblical
 * research, an interactive outline builder, autosaved drafts with revision
 * history, multi-format export, outline templates, sermon series planning,
 * and a curated illustrations library.
 *
 * Render-focused: drafts/revisions live in `useSermonDrafts`, autosave in
 * `useSermonAutosave`, and the panels (header, research, outline, drafts,
 * templates, illustrations, series-picker) are dedicated subcomponents
 * under `./sermon-prep/`.
 */
const SermonPrepTool = () => {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  // Core sermon state
  const [sermonTopic, setSermonTopic] = useState('');
  const [sermonNotes, setSermonNotes] = useState('');
  const [researchResults, setResearchResults] = useState(null);
  const [outline, setOutline] = useState(cloneDefaultOutline);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sendingSlides, setSendingSlides] = useState(false);

  // Series + tags
  const [seriesId, setSeriesId] = useState(null);
  const [seriesPosition, setSeriesPosition] = useState(null);
  const [tags, setTags] = useState([]);

  // Drafts state
  const [draftId, setDraftId] = useState(null);
  const [draftsOpen, setDraftsOpen] = useState(false);

  // Templates dialog
  const [templatesOpen, setTemplatesOpen] = useState(false);

  // Illustrations dialog (per outline-point context)
  const [illustrationsOpen, setIllustrationsOpen] = useState(false);
  const [illustrationsTarget, setIllustrationsTarget] = useState(null); // { id, title }
  // Notes picker dialog (per outline-point context)
  const [notesPickerOpen, setNotesPickerOpen] = useState(false);
  const [notesTarget, setNotesTarget] = useState(null); // { id, title }

  // Printable manuscript dialog
  const [manuscriptOpen, setManuscriptOpen] = useState(false);

  const { status: saveStatus, savedAt, resetStatus } = useSermonAutosave({
    topic: sermonTopic,
    notes: sermonNotes,
    outline,
    research: researchResults,
    draftId,
    setDraftId,
    seriesId,
    seriesPosition,
    tags,
  });

  const drafts = useSermonDrafts({
    t,
    setSermonTopic,
    setSermonNotes,
    setOutline,
    setResearchResults,
    setDraftId,
    draftId,
    setDraftsOpen,
    setSeriesId,
    setSeriesPosition,
    setTags,
  });

  const openDraftsDialog = () => {
    setDraftsOpen(true);
    drafts.fetchDrafts();
  };

  // Auto-open the drafts dialog whenever the URL carries a series/tag filter
  // — so links like /sermon-prep?series_id=Romans+Series land already filtered.
  // Also: if the URL carries `?draft_id=<id>` (e.g. from
  // "Build sermon from this conversation" in the AI Companion), load that
  // draft immediately so the editor pre-fills without an extra tap.
  // Runs once on mount; the chip strip itself stays bound to the URL after that.
  useEffect(() => {
    const seriesParam = searchParams.get('series_id');
    const tagParam = searchParams.get('tag');
    const draftIdParam = searchParams.get('draft_id');
    if (draftIdParam) {
      drafts.loadDraft(draftIdParam);
    } else if (seriesParam || tagParam) {
      openDraftsDialog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startNewSermon = () => {
    setSermonTopic('');
    setSermonNotes('');
    setOutline(cloneDefaultOutline());
    setResearchResults(null);
    setSeriesId(null);
    setSeriesPosition(null);
    setTags([]);
    setDraftId(null);
    resetStatus?.();
  };

  // Humanized "Saved 10s ago" label
  const [savedAgoLabel, setSavedAgoLabel] = useState('');
  useEffect(() => {
    if (!savedAt) return;
    const tick = () => {
      const secs = Math.max(0, Math.round((Date.now() - savedAt) / 1000));
      setSavedAgoLabel(
        secs < 5 ? t('sermon.justNow')
        : secs < 60 ? `${secs}s`
        : `${Math.round(secs / 60)}m`,
      );
    };
    tick();
    const interval = setInterval(tick, 10000);
    return () => clearInterval(interval);
  }, [savedAt, language]);

  const handleResearch = async () => {
    if (!sermonTopic.trim()) {
      toast.error(t('sermon.enterTopic'));
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/lumera/sermon-prep`,
        { topic: sermonTopic, notes: sermonNotes },
        { withCredentials: true },
      );
      if (response.data.success) {
        setResearchResults(response.data.research);
        toast.success(t('sermon.researchComplete'));
      }
    } catch (error) {
      console.error('Error researching sermon:', error);
      toast.error(t('sermon.researchError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Turn AI research into a ready-to-edit outline (intro + one point per main
  // point/theme, enriched with supporting verses, + conclusion with applications).
  const buildOutlineFromResearch = () => {
    const r = researchResults;
    if (!r) return;
    const sug = r.sermon_outline_suggestion || {};
    let id = Date.now();
    const points = [];
    const introVerse = r.key_verses?.[0];
    points.push({
      id: id++,
      title: t('sermon.introduction'),
      content: [sug.introduction, introVerse ? `${introVerse.reference} — "${introVerse.text}"` : '']
        .filter(Boolean).join('\n\n'),
    });
    const mains = Array.isArray(sug.main_points) && sug.main_points.length
      ? sug.main_points
      : (r.themes || []).map((th) => th.theme || th);
    mains.forEach((mp, i) => {
      const theme = (r.themes || [])[i];
      const verses = theme?.supporting_verses?.join(', ');
      points.push({
        id: id++,
        title: typeof mp === 'string' ? mp : (mp.theme || `${t('sermon.point')} ${i + 1}`),
        content: [theme?.explanation, verses ? `${t('sermon.verses')}: ${verses}` : '']
          .filter(Boolean).join('\n\n'),
      });
    });
    points.push({
      id: id++,
      title: t('sermon.conclusion'),
      content: [sug.conclusion, ...(r.application || []).slice(0, 3).map((a, i) => `${i + 1}. ${a}`)]
        .filter(Boolean).join('\n'),
    });
    setOutline(points);
    toast.success(t('sermon.outlineBuilt'));
  };

  const addOutlinePoint = () => {
    setOutline((prev) => [
      ...prev,
      { id: Date.now(), title: `Point ${prev.length}`, content: '' },
    ]);
  };

  const removeOutlinePoint = (id) => {
    setOutline((prev) => (prev.length > 2 ? prev.filter((p) => p.id !== id) : prev));
  };

  const updateOutlinePoint = (id, field, value) => {
    setOutline((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)),
    );
  };

  // Append illustration text to the targeted outline point's content.
  const insertIllustrationIntoTarget = (text) => {
    if (!illustrationsTarget) return;
    setOutline((prev) =>
      prev.map((p) => {
        if (p.id !== illustrationsTarget.id) return p;
        const sep = p.content && p.content.trim().length > 0 ? '\n\n' : '';
        return { ...p, content: `${p.content || ''}${sep}${text}` };
      }),
    );
  };

  const openIllustrationsForPoint = (id, title) => {
    setIllustrationsTarget({ id, title });
    setIllustrationsOpen(true);
  };

  // Append a saved note's text to the targeted outline point's content.
  const insertNoteIntoTarget = (text) => {
    if (!notesTarget || !text) return;
    setOutline((prev) =>
      prev.map((p) => {
        if (p.id !== notesTarget.id) return p;
        const sep = p.content && p.content.trim().length > 0 ? '\n\n' : '';
        return { ...p, content: `${p.content || ''}${sep}${text}` };
      }),
    );
  };

  const openNotesForPoint = (id, title) => {
    setNotesTarget({ id, title });
    setNotesPickerOpen(true);
  };

  const copyToClipboard = () => {
    let copyText = `${sermonTopic}\n\n`;
    outline.forEach((point, idx) => {
      copyText += `${idx + 1}. ${point.title}\n${point.content}\n\n`;
    });
    navigator.clipboard.writeText(copyText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success(t('sermon.copied'));
  };

  const sermonPayload = {
    topic: sermonTopic,
    notes: sermonNotes,
    outline,
    research: researchResults,
  };

  const handleSendToSlidecon = async () => {
    if (!sermonTopic.trim()) {
      toast.error(t('sermon.enterTopicFirst'));
      return;
    }
    setSendingSlides(true);
    const pending = toast.loading(
      t('sermon.creatingSlides'),
    );
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/lumera/sermon-prep/send-to-slidecon`,
        sermonPayload,
        { withCredentials: true },
      );
      const url = res.data?.edit_url || res.data?.view_url;
      toast.dismiss(pending);
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        toast.success(t('sermon.slidesCreatedOpen'));
      } else {
        toast.success(t('sermon.slidesCreated'));
      }
    } catch (error) {
      toast.dismiss(pending);
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 402) {
        toast.error(detail || t('sermon.upgradeForSlides'));
      } else if (status === 409) {
        toast.error(detail || t('sermon.slideconLimit'));
      } else if (status === 503) {
        toast.error(detail || t('sermon.slideconNotConfigured'));
      } else {
        toast.error(detail || t('sermon.couldNotCreateSlides'));
      }
    } finally {
      setSendingSlides(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 dark:from-slate-900 dark:to-slate-800">
      <SermonPrepHeader
        language={language}
        navigate={navigate}
        saveStatus={saveStatus}
        savedAgoLabel={savedAgoLabel}
        openDraftsDialog={openDraftsDialog}
        openTemplatesDialog={() => setTemplatesOpen(true)}
        startNewSermon={startNewSermon}
        copyToClipboard={copyToClipboard}
        copied={copied}
        onExportTxt={() => {
          exportSermonAsTxt(sermonPayload, language);
          toast.success(t('sermon.sermonExported'));
        }}
        onExportMarkdown={() => {
          exportSermonAsMarkdown(sermonPayload);
          toast.success(t('sermon.markdownExported'));
        }}
        onExportPdf={() => exportSermonAsPdf(sermonPayload)}
        onExportManuscript={() => setManuscriptOpen(true)}
        onSendToSlidecon={handleSendToSlidecon}
        sendingSlides={sendingSlides}
        onStartPreaching={() => {
          if (draftId) {
            navigate(`/sermon-prep/present?draft_id=${encodeURIComponent(draftId)}`);
          } else {
            // Unsaved draft — pass the in-memory state via router state
            navigate('/sermon-prep/present', {
              state: {
                draft: {
                  draft_id: null,
                  topic: sermonTopic,
                  outline,
                  notes: sermonNotes,
                  research: researchResults,
                  series_id: seriesId || '',
                  series_position: seriesPosition || '',
                },
              },
            });
          }
        }}
      />

      <main className="p-4 sm:p-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            <SermonResearchPanel
              language={language}
              sermonTopic={sermonTopic}
              setSermonTopic={setSermonTopic}
              sermonNotes={sermonNotes}
              setSermonNotes={setSermonNotes}
              isLoading={isLoading}
              onResearch={handleResearch}
              researchResults={researchResults}
              onBuildOutline={buildOutlineFromResearch}
            />
            <SermonSeriesPicker
              language={language}
              seriesId={seriesId}
              setSeriesId={setSeriesId}
              seriesPosition={seriesPosition}
              setSeriesPosition={setSeriesPosition}
              tags={tags}
              setTags={setTags}
            />
          </div>
          <div>
            <SermonOutlinePanel
              language={language}
              outline={outline}
              onAddPoint={addOutlinePoint}
              onRemovePoint={removeOutlinePoint}
              onUpdatePoint={updateOutlinePoint}
              onAddIllustration={openIllustrationsForPoint}
              onInsertFromNotes={openNotesForPoint}
            />
            <div className="mt-4">
              <SermonAiScaffold topic={sermonTopic} scripture="" outline={outline} />
            </div>
          </div>
        </div>

        {/* Create-slides CTA — turn the finished sermon into a Slidecon deck */}
        <div
          className="mt-6 rounded-2xl border border-cyan-200 dark:border-cyan-900/50 bg-gradient-to-r from-cyan-50 to-amber-50 dark:from-slate-800 dark:to-slate-800 p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between"
          data-testid="sermon-slidecon-cta"
        >
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-xl bg-cyan-600/10 text-cyan-700 dark:text-cyan-300 shrink-0">
              <Layers className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">
                {t('sermon.turnIntoSlides')}
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-300 mt-0.5 max-w-xl">
                {t('sermon.turnIntoSlidesDesc')}
              </p>
            </div>
          </div>
          <Button
            onClick={handleSendToSlidecon}
            disabled={sendingSlides}
            size="lg"
            className="bg-cyan-600 hover:bg-cyan-700 text-white shrink-0 w-full sm:w-auto"
            data-testid="sermon-slidecon-cta-btn"
          >
            {sendingSlides ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {t('sermon.creating')}</>
            ) : (
              <><Layers className="w-5 h-5 mr-2" /> {t('sermon.createSlides')}</>
            )}
          </Button>
        </div>
      </main>

      <SermonDraftsDialog
        open={draftsOpen}
        onOpenChange={setDraftsOpen}
        language={language}
        draftId={draftId}
        draftsList={drafts.draftsList}
        loadingDrafts={drafts.loadingDrafts}
        revisionsForId={drafts.revisionsForId}
        revisions={drafts.revisions}
        loadingRevisions={drafts.loadingRevisions}
        onLoadDraft={drafts.loadDraft}
        onDeleteDraft={drafts.deleteDraft}
        onOpenRevisions={drafts.openRevisions}
        onRevertTo={drafts.revertTo}
      />

      <SermonTemplatesDialog
        open={templatesOpen}
        onOpenChange={setTemplatesOpen}
        language={language}
        topic={sermonTopic}
        notes={sermonNotes}
        onApplyOutline={setOutline}
      />

      <SermonIllustrationsDialog
        open={illustrationsOpen}
        onOpenChange={(o) => {
          setIllustrationsOpen(o);
          if (!o) setIllustrationsTarget(null);
        }}
        language={language}
        topic={sermonTopic}
        pointTitle={illustrationsTarget?.title || ''}
        onInsert={insertIllustrationIntoTarget}
      />

      <NotesPickerDialog
        open={notesPickerOpen}
        onOpenChange={(o) => {
          setNotesPickerOpen(o);
          if (!o) setNotesTarget(null);
        }}
        pointTitle={notesTarget?.title || ''}
        onInsert={insertNoteIntoTarget}
      />

      <SermonManuscriptDialog
        open={manuscriptOpen}
        onOpenChange={setManuscriptOpen}
        language={language}
        defaultSeriesName={seriesId || ''}
        defaultSeriesPosition={seriesPosition || ''}
        defaultPreacher={user?.name || user?.email || ''}
        onPrint={(info) => {
          exportSermonManuscriptPdf(sermonPayload, info);
          toast.success(t('sermon.manuscriptPrinted'));
        }}
      />
    </div>
  );
};

export default SermonPrepTool;
